<template>
  <div class="min-h-screen bg-gray-50">
    <AppHeader />

    <section class="relative h-64 flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img
          :alt="$t('coreService.hero.imageAlt')"
          class="w-full h-full object-cover opacity-40"
          src="/images/coreService/service-hero-bg.jpg"
        >
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-4xl md:text-5xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('coreService.hero.title') }}
        </h1>
        <p class="text-lg md:text-xl max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('coreService.hero.subtitle') }}
        </p>
      </div>
    </section>

    <main class="py-16 px-6 md:px-12 max-w-7xl mx-auto">
      <section class="mb-12 rounded-2xl bg-white p-8 md:p-10 shadow-lg border border-purple-100">
        <p class="text-sm font-semibold text-purple-600 mb-3 uppercase tracking-wider">
          {{ $t('coreService.portfolio.eyebrow') }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          {{ $t('coreService.portfolio.introTitle') }}
        </h2>
        <p class="text-lg text-gray-600 leading-8 max-w-5xl">
          {{ $t('coreService.portfolio.introDescription') }}
        </p>
      </section>

      <div class="core-service-layout">
        <aside>
          <div class="core-service-sidebar bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-xl font-bold mb-6 text-gray-900">{{ $t('coreService.nav.title') }}</h3>
            <ul class="space-y-3">
              <li v-for="section in serviceSections" :key="section.id">
                <a
                  :href="`#${section.id}`"
                  :class="[
                    'service-nav-link flex items-center px-4 py-3 rounded-lg transition-colors duration-300',
                    activeSection === section.id
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                  ]"
                  @click.prevent="scrollToSection(section.id)"
                >
                  <i :class="[section.icon, 'mr-3 text-xl']"></i>
                  <span>{{ section.title }}</span>
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <section class="space-y-12">
          <article
            v-for="section in serviceSections"
            :id="section.id"
            :key="section.id"
            class="service-section bg-white rounded-xl shadow-lg overflow-hidden scroll-mt-28"
          >
            <div class="h-64 md:h-80 overflow-hidden">
              <img
                :src="section.image"
                :alt="section.title"
                class="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>

            <div class="p-8 md:p-10">
              <div class="flex items-center mb-6">
                <div
                  class="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mr-5 shrink-0"
                >
                  <i :class="[section.icon, 'text-3xl text-purple-600']"></i>
                </div>
                <div>
                  <p class="text-sm font-semibold text-purple-600 mb-1">
                    {{ section.subtitle }}
                  </p>
                  <h2 class="text-2xl md:text-3xl font-bold text-gray-900">
                    {{ section.title }}
                  </h2>
                </div>
              </div>

              <p class="text-gray-600 text-lg leading-8 mb-8">
                {{ section.description }}
              </p>

              <div class="mb-9">
                <h3 class="text-xl md:text-2xl font-bold text-gray-900 mb-5">
                  {{ $t('coreService.portfolio.serviceContentTitle') }}
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    v-for="item in section.serviceItems"
                    :key="item.text"
                    class="flex items-start p-4 rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <i :class="[item.icon || 'fas fa-check', 'text-purple-500 text-lg mt-1 mr-3']"></i>
                    <p class="text-gray-700 leading-7">{{ item.text }}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 class="text-xl md:text-2xl font-bold text-gray-900 mb-5">
                  {{ $t('coreService.portfolio.coreValueTitle') }}
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    v-for="value in section.valueItems"
                    :key="value.title"
                    class="rounded-xl border border-purple-100 bg-purple-50/50 p-5"
                  >
                    <div class="flex items-center mb-3">
                      <div
                        class="w-10 h-10 rounded-lg bg-white flex items-center justify-center mr-3 shadow-sm"
                      >
                        <i :class="[value.icon || 'fas fa-star', 'text-purple-600 text-lg']"></i>
                      </div>
                      <h4 class="font-bold text-gray-900">{{ value.title }}</h4>
                    </div>
                    <ul v-if="value.points?.length" class="space-y-2">
                      <li
                        v-for="point in value.points"
                        :key="point"
                        class="flex items-start text-gray-700 text-sm leading-6"
                      >
                        <i class="fas fa-circle text-[6px] text-purple-500 mt-2 mr-2 shrink-0"></i>
                        <span>{{ point }}</span>
                      </li>
                    </ul>
                    <p v-else class="text-gray-700 text-sm leading-6">
                      {{ value.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </section>
      </div>
    </main>

    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AppHeader from '../components/AppHeader.vue';
import AppFooter from '../components/AppFooter.vue';

const { tm } = useI18n();

const serviceSections = computed(() => {
  const sections = tm('coreService.portfolio.sections');
  return Array.isArray(sections) ? sections : [];
});

const sectionIds = computed(() => serviceSections.value.map((section) => section.id));
const activeSection = ref('digital-workplace');

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    activeSection.value = sectionId;
  }
};

const checkActiveSection = () => {
  const ids = sectionIds.value;
  for (const sectionId of ids) {
    const element = document.getElementById(sectionId);
    if (!element) continue;

    const rect = element.getBoundingClientRect();
    if (rect.top <= 150 && rect.bottom >= 150) {
      activeSection.value = sectionId;
      break;
    }
  }
};

onMounted(() => {
  const firstSection = sectionIds.value[0];
  if (firstSection) {
    activeSection.value = firstSection;
  }
  window.addEventListener('scroll', checkActiveSection);
  checkActiveSection();
});

onUnmounted(() => {
  window.removeEventListener('scroll', checkActiveSection);
});
</script>

<style scoped>
.core-service-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
}

.core-service-sidebar {
  position: relative;
}

@media (min-width: 1024px) {
  .core-service-layout {
    grid-template-columns: 18rem minmax(0, 1fr);
    align-items: start;
  }

  .core-service-sidebar {
    position: sticky;
    top: 6rem;
  }
}

.service-nav-link {
  position: relative;
  overflow: hidden;
}

.service-nav-link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background-color: currentColor;
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.service-nav-link:hover::before,
.service-nav-link.bg-purple-600::before {
  transform: scaleY(1);
}
</style>
